import React from "react";
import { ArrowRight, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import Button from "../components/Button";
import { SECTION_IDS, ZONES } from "../landingConfig";

/**
 * Hero: the promise, the two CTAs, and the risk-zone strip.
 *
 * The strip is built in the DOM rather than shipped as an image — it costs no
 * bytes, cannot shift the layout, scales to any width and is fully translated.
 * Every zone shows fill + name + count + icon, so it never relies on colour.
 *
 * Fabricated demo data for one class of 28. Deliberately close to the 26%
 * at-risk figure from the pilot (8 of 28 here) so the strip and the stat band
 * tell the same story.
 */
const CLASS_SIZE = 28;
const DISTRIBUTION = { green_zone: 20, yellow_zone: 5, orange_zone: 2, red_zone: 1 };

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section id="top" className="px-4 pb-16 pt-10 sm:px-6 md:pb-24 md:pt-16 lg:px-8">
      <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <p className="text-sm font-medium uppercase tracking-wide text-primary">
            {t("landing.hero.eyebrow")}
          </p>
          <h1 className="mt-4 text-4xl font-bold leading-tight text-black-landing md:text-6xl">
            {t("landing.hero.h1")}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted md:text-xl">
            {t("landing.hero.sub")}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button href={`#${SECTION_IDS.contact}`}>
              {t("landing.hero.cta_primary")}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>

        {/* Risk-zone strip */}
        <div className="rounded-2xl border border-line bg-white p-6">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-base font-semibold text-black-landing">
              {t("landing.hero.zones_title")}
            </h2>
            <span className="inline-flex items-center gap-1.5 text-sm text-muted">
              <Users className="h-4 w-4" aria-hidden="true" />
              <span className="tabular-nums">
                {t("landing.hero.students", { count: CLASS_SIZE })}
              </span>
            </span>
          </div>

          {/* Proportional bar. Widths are static, so nothing animates or shifts. */}
          <div
            className="mt-5 flex h-4 w-full overflow-hidden rounded-full"
            role="img"
            aria-label={ZONES.map(
              (z) => `${t(`zones.${z.code}`)}: ${DISTRIBUTION[z.code]}`
            ).join(", ")}
          >
            {ZONES.map((zone) => (
              <span
                key={zone.code}
                className={zone.bg}
                style={{ width: `${(DISTRIBUTION[zone.code] / CLASS_SIZE) * 100}%` }}
              />
            ))}
          </div>

          <ul className="mt-5 flex flex-col gap-2">
            {ZONES.map((zone) => {
              const { code, bg, Icon } = zone;
              return (
                <li
                  key={code}
                  className="flex items-center gap-3 rounded-lg border border-line px-3 py-2"
                >
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${bg}`}
                  >
                    <Icon
                      className="h-4 w-4 text-black-landing"
                      aria-hidden="true"
                    />
                  </span>
                  <span className="flex-1 text-sm font-medium text-black-landing">
                    {t(`zones.${code}`)}
                  </span>
                  <span className="text-sm tabular-nums text-muted">
                    {DISTRIBUTION[code]}
                  </span>
                </li>
              );
            })}
          </ul>

          <p className="mt-4 text-xs text-muted">{t("landing.hero.zones_note")}</p>
        </div>
      </div>
    </section>
  );
}
